/**
 * @file TokenRefreshGuard.ts
 * @description Modulo centralizzato per la gestione proattiva del refresh JWT.
 *
 * Utilizzato dall'interceptor request di axios per verificare la freschezza del token
 * PRIMA di ogni richiesta API. Se il token è scaduto o prossimo alla scadenza,
 * esegue un refresh silenzioso tramite MSAL.
 *
 * Include un lock (Promise condivisa) per evitare refresh concorrenti quando
 * più richieste partono simultaneamente con token scaduto.
 *
 * Espone anche un evento 'jwt-refresh-needed' per permettere ad altri moduli
 * (keepalive, response interceptor) di triggerare un refresh on-demand.
 */

import { jwtDecode } from 'jwt-decode';
import { msalInstance } from './loginMSALAuthProcess';

// Soglia in secondi: se il token scade entro questo tempo, esegue il refresh
const TOKEN_EXPIRY_THRESHOLD_SECONDS = 60;

// Timeout massimo per il refresh MSAL (ms)
const REFRESH_TIMEOUT_MS = 10_000;

// Lock per evitare refresh concorrenti
let refreshInProgress: Promise<string | null> | null = null;

/**
 * Controlla se il token JWT corrente è scaduto o prossimo alla scadenza.
 * Decodifica il token dall'header Authorization di axios.
 *
 * @param token - Il token JWT da verificare
 * @returns true se il token è scaduto o scade entro TOKEN_EXPIRY_THRESHOLD_SECONDS
 */
export const isTokenExpiredOrExpiring = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    const nowSec = Math.floor(Date.now() / 1000);
    return decoded.exp - nowSec <= TOKEN_EXPIRY_THRESHOLD_SECONDS;
  } catch {
    // Se il token non è decodificabile, lo consideriamo scaduto
    return true;
  }
};

/**
 * Estrae il token JWT dall'header Authorization.
 * @param authHeader - Il valore dell'header Authorization (es. "Bearer xxx")
 * @returns Il token JWT o null se non presente
 */
export const extractTokenFromAuthHeader = (
  authHeader: string | undefined,
): string | null => {
  if (!authHeader || !authHeader.startsWith('Bearer ')) return null;
  return authHeader.substring(7);
};

/**
 * Esegue il refresh del token tramite MSAL acquireTokenSilent.
 * Usa un lock per evitare refresh concorrenti: se un refresh è già in corso,
 * ritorna la stessa Promise.
 *
 * @returns Il nuovo token, o null se il refresh fallisce
 */
export const refreshTokenSilently = async (): Promise<string | null> => {
  // Se un refresh è già in corso, attendi il risultato
  if (refreshInProgress) {
    return refreshInProgress;
  }

  refreshInProgress = executeRefresh();

  try {
    return await refreshInProgress;
  } finally {
    refreshInProgress = null;
  }
};

/**
 * Indica se un refresh è attualmente in corso.
 * Utile per il watchdog per evitare refresh sovrapposti.
 */
export const isRefreshInProgress = (): boolean => refreshInProgress !== null;

const executeRefresh = async (): Promise<string | null> => {
  try {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      console.warn('[TokenRefreshGuard] Nessun account MSAL attivo');
      return null;
    }

    const request = {
      scopes: [
        `https://${import.meta.env.VITE_NX_PUBLIC_YOUR_TENANT_NAME}.onmicrosoft.com/focuscloud/All`,
      ],
      account,
      forceRefresh: true,
    };

    // Race tra il refresh MSAL e un timeout
    const result = await Promise.race([
      msalInstance.acquireTokenSilent(request),
      new Promise<null>((_, reject) =>
        setTimeout(
          () => reject(new Error('[TokenRefreshGuard] Timeout refresh MSAL')),
          REFRESH_TIMEOUT_MS,
        ),
      ),
    ]);

    if (!result || !result.accessToken) {
      console.warn(
        '[TokenRefreshGuard] acquireTokenSilent ha restituito un token nullo',
      );
      return null;
    }

    console.log('[TokenRefreshGuard] Token refreshato con successo');
    return result.accessToken;
  } catch (error) {
    console.error('[TokenRefreshGuard] Errore durante il refresh:', error);
    return null;
  }
};

/**
 * Emette l'evento globale 'jwt-refresh-needed'.
 * Usato dal keepalive e dal response interceptor per triggerare
 * un refresh on-demand gestito da useLoginMsal.
 */
export const emitJwtRefreshNeeded = (): void => {
  window.dispatchEvent(new CustomEvent('jwt-refresh-needed'));
};
